import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PersonalReductionRequisitionWithRelations } from '@api-swagger-client/index';
import {
    ApprovalChainItem,
    StatusTone,
    fullName,
    initials,
    statusIcon,
    statusTone,
} from '@app/@shared/approval-detail/approval-detail.utils';
import { LinkExpirationModule } from '@app/@shared/link-expiration/link-expiration.module';
import { TemporalLinkService } from '@app/@shared/temporal-link.service';
import { PersonalReductionsService } from '@app/client/personal-reductions/personal-reductions.service';
import { MessageService, SharedModule } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { timer } from 'rxjs';

@Component({
    selector: 'app-reduction-authorization',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        LinkExpirationModule,
        AvatarModule,
        ButtonModule,
        DialogModule,
        InputTextareaModule,
        ProgressSpinnerModule,
        ToastModule,
        SharedModule,
    ],
    providers: [MessageService],
    templateUrl: './reduction-authorization.component.html',
    styleUrl: './reduction-authorization.component.scss',
})
export class ReductionAuthorizationComponent implements OnInit {
    token: string | null = null;
    referenceId: string | null = null;
    loading: boolean = true;
    error: string | null = null;
    success: boolean = false;
    verifying: boolean = false;

    expirationDate: Date | null = null;
    linkExpired: boolean = false;
    linkDetails: any = null;

    reduction: PersonalReductionRequisitionWithRelations | null = null;
    processingAction = false;

    // Dialogs
    submitDialogVisible = false;
    approveDialogVisible = false;
    rejectDialogVisible = false;
    commentText = '';
    commentError = false;

    // Available actions for the current user
    availableActions: string[] = [];

    // Responsables de Capital Humano; se arma al cargar para no recrear el arreglo en cada ciclo
    approvalChain: ApprovalChainItem[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private temporalLinkService: TemporalLinkService,
        private messageService: MessageService,
        private personalReductionsService: PersonalReductionsService
    ) {}

    ngOnInit(): void {
        // Extraer los parámetros de la URL
        this.activatedRoute.queryParams.subscribe((params) => {
            this.token = params['token'];
            this.referenceId = params['referenceId'];
            if (this.token) {
                this.validateLink();
            } else {
                this.error = 'Enlace inválido: token no encontrado';
                this.loading = false;
            }
        });
    }

    validateLink(): void {
        if (!this.token) {
            this.error = 'Token no proporcionado';
            this.loading = false;
            return;
        }

        this.temporalLinkService.validateTemporalLink(this.token).subscribe({
            next: (response) => {
                if (response && response.valid) {
                    // Marcamos como exitoso (el link es válido)
                    this.success = true;
                    this.linkDetails = response.link;

                    // El conteo y el aviso de expiración los pinta <app-link-expiration>
                    if (this.linkDetails.expirationDate) {
                        this.expirationDate = new Date(
                            this.linkDetails.expirationDate
                        );
                        this.linkExpired = this.expirationDate <= new Date();
                    }

                    if (response.valid == true && !response.link['used']) {
                       this.getReductionDetail()
                    }

                    // Si ya está usado, mostramos un mensaje después de un breve retraso
                    if (this.linkDetails.used) {
                        timer(1000).subscribe(() => {
                            this.messageService.add({
                                severity: 'info',
                                summary: 'Email ya verificado',
                                detail: 'Este correo electrónico ya ha sido verificado anteriormente',
                                life: 3000,
                            });
                        });
                    }
                } else {
                    this.error = 'El enlace de verificación no es válido';
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = err.message || 'Error al verificar el email';
                this.loading = false;
                console.error('Error al verificar el email:', err);

                // Mostrar mensaje de error usando MessageService
                timer(500).subscribe(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error de verificación',
                        detail:
                            err.error?.message ||
                            'Ha ocurrido un error al verificar el correo',
                        life: 3000,
                    });
                });
            },
        });
    }
    onExpirationStatusChanged(expired: boolean): void {
        this.linkExpired = expired;
    }

    getStatusTone(statusCode: string): StatusTone {
        return statusTone(statusCode);
    }

    getStatusIcon(statusCode: string): string {
        return statusIcon(statusCode);
    }

    fullName = fullName;
    initials = initials;

    /**
     * Obtiene la clase CSS para el tipo de movimiento
     */
    getMovementTypeClass(movementType: number | string): string {
        if (movementType === 1 || movementType === 'Renuncia') {
            return 'type-resignation';
        } else if (movementType === 2 || movementType === 'Despido') {
            return 'type-dismissal';
        }
        return '';
    }

    /**
     * Obtiene la etiqueta para el tipo de movimiento
     */
    getMovementTypeLabel(movementType: number | string): string {
        if (movementType === 1) {
            return 'Renuncia';
        } else if (movementType === 2) {
            return 'Despido';
        }
        return movementType as string;
    }

    /**
     * Obtiene el color de la empresa directamente del objeto company
     */
    getCompanyColor(company: any): string {
        // Si no hay empresa o no tiene color, retorna un color por defecto
        if (!company || !company.color) {
            return '#CCCCCC'; // Color gris por defecto
        }

        // Usa directamente el color de la empresa
        return company.color;
    }

    // Actions handling
    hasAction(action: string): boolean {
        return this.availableActions.includes(action);
    }

    submitReduction(): void {
        this.submitDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    approveReduction(): void {
        this.approveDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    rejectReduction(): void {
        this.rejectDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    cancelDialog(): void {
        this.submitDialogVisible = false;
        this.approveDialogVisible = false;
        this.rejectDialogVisible = false;
        this.commentText = '';
        this.commentError = false;
    }

    confirmSubmit(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalReductionsService
            .submitForApproval(this.referenceId!, {
                comments: this.commentText,
            })
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Solicitud enviada',
                        detail: 'La solicitud de baja ha sido enviada para aprobación',
                    });
                    this.submitDialogVisible = false;
                    this.processingAction = false;
                    this.getReductionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al enviar la solicitud',
                    });
                    this.processingAction = false;
                },
            });
    }

    confirmApprove(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalReductionsService
            .approveRequisitionPublic(this.referenceId!,this.token, {
                comments: this.commentText,
            })
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Solicitud aprobada',
                        detail: 'La solicitud de baja ha sido aprobada correctamente',
                    });
                    this.approveDialogVisible = false;
                    this.processingAction = false;
                    this.getReductionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al aprobar la solicitud',
                    });
                    this.processingAction = false;
                },
            });
    }

    confirmReject(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalReductionsService
            .rejectRequisitionPublic(this.referenceId!,this.token, {
                comments: this.commentText,
            })
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Solicitud rechazada',
                        detail: 'La solicitud de baja ha sido rechazada',
                    });
                    this.rejectDialogVisible = false;
                    this.processingAction = false;
                    this.getReductionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al rechazar la solicitud',
                    });
                    this.processingAction = false;
                },
            });
    }

    private async getReductionDetail(): Promise<void> {
        this.loading = true;

        try {
            // Get reduction details
            this.personalReductionsService
                .findByIdFromLink(this.referenceId!,this.token)
                .subscribe({
                    next: (response) => {
                        this.reduction = response['data'];
                        this.approvalChain = this.buildApprovalChain();
                        this.availableActions = response['data']['actions'] || [];

                        //this.loadAvailableActions();
                        // Sort histories by date (newest first)
                        if (this.reduction?.requisitionStatusHistories) {
                            this.reduction.requisitionStatusHistories.sort(
                                (a, b) =>
                                    new Date(b.createdOn).getTime() -
                                    new Date(a.createdOn).getTime()
                            );
                        }

                        this.loading = false;
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'No se pudo cargar la información de la solicitud de baja',
                        });
                        this.loading = false;
                    },
                });
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar la solicitud de baja',
            });
            this.loading = false;
        }
    }

    private buildApprovalChain(): ApprovalChainItem[] {
        const hc = this.reduction?.humanCapitalCompany;
        if (!hc) {
            return [];
        }
        return [
            { role: 'Gerente de Capital Humano', person: hc.humanCapitalManager },
            { role: 'Director de Capital Humano', person: hc.humanCapitalDirector },
            { role: 'Gerente de Nómina', person: hc.payrollManager },
        ];
    }
}
